import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import { slugify } from '../../utils/slug';
import WoodenPressIcon from '../utils/WoodenPressIcon';
import './MenuLoading.css';

class Menu extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isMenuClosing: false,
            openMenuKey: null,
            mobileOpen: false,
        };

        this.closeMenu = this.closeMenu.bind(this);
        this.resetMenu = this.resetMenu.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleDropdownClick = this.handleDropdownClick.bind(this);
        this.menuRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleDocumentClick);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleDocumentClick);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.isScrolled !== this.props.isScrolled && (this.state.openMenuKey || this.state.isMenuClosing)) {
            this.setState({isMenuClosing: false, openMenuKey: null, mobileOpen: false});
        }
    }

    handleDocumentClick(event) {
        if (this.menuRef.current && !this.menuRef.current.contains(event.target)) {
            this.setState({isMenuClosing: false, openMenuKey: null, mobileOpen: false});
        }
    }

    handleDropdownClick(event, menuKey) {
        event.preventDefault();
        this.setState((state) => ({
            isMenuClosing: false,
            openMenuKey: state.openMenuKey === menuKey ? null : menuKey,
        }));
    }

    closeMenu() {
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
        }

        this.setState({isMenuClosing: true, openMenuKey: null, mobileOpen: false});
    }

    resetMenu() {
        if (this.state.isMenuClosing || this.state.openMenuKey) {
            this.setState({isMenuClosing: false, openMenuKey: null});
        }
    }

    getCategoryLabel(category) {
        return category.main_category_name || category.sub_category_name || category.final_category_name || '';
    }

    getCategoryId(category) {
        return category.final_category_id || category.sub_category_id || category.main_category_id || category.id || '';
    }

    getChildCategories(category) {
        const subCategories = Array.isArray(category.sub_category_list) ? category.sub_category_list : [];
        const finalCategories = Array.isArray(category.final_category_list) ? category.final_category_list : [];

        return subCategories.length > 0 ? subCategories : finalCategories;
    }

    renderCategoryItems(categories, depth = 0) {
        return categories.map((category, index) => {
            const label = this.getCategoryLabel(category);
            const children = this.getChildCategories(category);
            const categoryId = this.getCategoryId(category);
            const hasChildren = children.length > 0;
            const categorySlug = slugify(category.slug || label);
            const labelContent = <>{label}{depth === 0 && categorySlug === 'cold-pressed-oils' ? <WoodenPressIcon /> : null}</>;

            if (hasChildren) {
                const menuKey = `category-${categoryId || index}`;
                const isOpen = this.state.openMenuKey === menuKey;
                const categoryPath = `/category/${slugify(category.slug || label)}?scope=category`;

                return (
                    <li className={`menu-item-has-children ${isOpen ? 'menu-item-open' : ''}`} key={`${label}-${categoryId || index}`}>
                        <Link to={categoryPath} onClick={this.closeMenu}>{labelContent}</Link>
                        <button type="button" className="sub-toggle" aria-label={`Toggle ${label} subcategories`} aria-expanded={isOpen}
                            onClick={(event) => this.handleDropdownClick(event, menuKey)}><i className="fa fa-chevron-down" aria-hidden="true"></i></button>
                        <ul className="sub-menu">
                            {this.renderCategoryItems(children, depth + 1)}
                        </ul>
                    </li>
                );
            }

            const categoryPath = `/category/${slugify(category.slug || label)}`;

            return (
                <li key={`${label}-${categoryId || index}`}>
                    <Link to={categoryPath} onClick={this.closeMenu}>{labelContent}</Link>
                </li>
            );
        });
    }

    render() {
        const menuClassName = `ps-main-menu ${this.state.isMenuClosing ? 'ps-main-menu--closing' : ''} ${this.state.mobileOpen ? 'ps-main-menu--mobile-open' : ''}`;
        const menu = Array.isArray(this.props.menu) ? this.props.menu : [];
        const loading = Boolean(this.props.loading);

        return (
            <nav className={menuClassName} aria-label="Main navigation" onMouseLeave={this.resetMenu}
                onKeyDown={(event) => { if (event.key === 'Escape') this.closeMenu(); }} ref={this.menuRef}>
                <button type="button" className="nw-mobile-menu-toggle" aria-label={this.state.mobileOpen ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={this.state.mobileOpen} aria-controls="nw-primary-menu"
                    onClick={() => this.setState((state) => ({mobileOpen: !state.mobileOpen, isMenuClosing: false, openMenuKey: null}))}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
                        {this.state.mobileOpen ? <path d="m6 6 12 12M6 18 18 6" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
                    </svg>
                </button>
                {loading && <span className="nw-menu-loading-status" role="status">Loading categories…</span>}
                <ul className="menu" id="nw-primary-menu" aria-busy={loading}>
                    <li><Link to="/" onClick={this.closeMenu}>Home</Link></li>
                    {loading ? [96, 112, 84, 96].map((width, index) => <li className="nw-menu-placeholder" key={index} aria-hidden="true"><span style={{'--menu-placeholder-width':`${width}px`}}/></li>) : this.renderCategoryItems(menu)}
                    <li className="ps-menu-best-deals">
                        <Link to="/" onClick={this.closeMenu}>
                            <span>Best Deals</span>
                            <span className="ps-menu-best-deals__tag" aria-hidden="true">
                                <svg width="23" height="23" viewBox="0 0 24 24" fill="none" focusable="false">
                                    <path d="M3 3h9l9 9-9 9-9-9V3Z" fill="currentColor" stroke="#fff" strokeWidth="1.2" strokeLinejoin="round" />
                                    <circle cx="7" cy="7" r="1.5" fill="#fff8e9" />
                                    <path d="m10 15 5-5" stroke="#fff8e9" strokeWidth="1.4" strokeLinecap="round" />
                                    <circle cx="10" cy="11" r="1" fill="#fff8e9" />
                                    <circle cx="15" cy="15" r="1" fill="#fff8e9" />
                                </svg>
                            </span>
                        </Link>
                    </li>
                </ul>
            </nav>
        );
    }
}

export default Menu;
